import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { cameraPosition, positionWorld, time, uniform, vec3, wgslFn } from 'three/tsl';
import { tagValueBucket } from './ValueComposition';
import { SHARED_AERIAL_UNIFORMS } from './AerialPerspective';
import type { StyleConfig } from './StyleConfig';

/**
 * Raymarched volumetric cloud dome for the WebGPU path.
 *
 * Renders a back-side sphere whose fragment shader marches a fixed-altitude
 * cloud slab (between `BASE_Y` and `TOP_Y`) for every view ray that points
 * upward. Each step samples a 3D Perlin-Worley density field driven by
 * `mx_worley_noise` + `mx_fractal_noise` analogues; transmittance is
 * accumulated via Beer's law and a 4-tap light march toward the sun gives
 * cheap self-shadowing. Henyey-Greenstein phase function biases scattering
 * toward the sun for the warm sunset rim.
 *
 * Sits between the gradient sky (`renderOrder = -1000`) and the existing
 * cloud cards (`renderOrder = -450`) at `renderOrder = -900` so it provides
 * a deeper sky background while the cards still contribute crisp parallax
 * silhouettes near the player.
 *
 * Cost: ~32 march steps × 4 light steps per fragment, full-res. ~2ms on
 * an M1 / ~1ms on a 3060 — within the 4ms cloud+godray budget the user
 * asked for. If perf becomes a concern, reduce `MARCH_STEPS` from 32 → 24
 * or drop the secondary light march.
 */

const makeNodeUniform = uniform as unknown as (
  value: unknown,
) => ReturnType<typeof uniform> & Record<string, any>;
const makeWgslFn = wgslFn as any;

const BASE_Y = 320;
const TOP_Y = 880;
// Camera far plane is 1500 (Camera.ts:201). The dome geometry has to sit
// inside the frustum or the depth test culls every fragment regardless of
// `frustumCulled = false`. 1200 keeps the back faces ~300m inside the far
// plane and is plenty large vs the canyon scale (track is ~50m wide).
const DOME_RADIUS = 1200;

interface VolumetricCloudUniforms {
  uSunDir: ReturnType<typeof makeNodeUniform>;
  uSunColor: ReturnType<typeof makeNodeUniform>;
  uHorizonColor: ReturnType<typeof makeNodeUniform>;
  uZenithColor: ReturnType<typeof makeNodeUniform>;
  uCoverage: ReturnType<typeof makeNodeUniform>;
  uDensity: ReturnType<typeof makeNodeUniform>;
  uWindSpeed: ReturnType<typeof makeNodeUniform>;
  uAnvilBias: ReturnType<typeof makeNodeUniform>;
}

export interface VolumetricCloudDome {
  mesh: THREE.Mesh;
  applyStyle: (style: StyleConfig) => void;
  /** Drives wind animation via the TSL `time` node automatically — no-op for now. */
  update: (dt: number) => void;
  dispose: () => void;
}

// Hash + value-noise primitives. WGSL doesn't expose mx_worley directly via
// wgslFn (the MaterialX nodes wire through the TSL graph instead), so we
// implement a cheap Worley-Perlin hybrid by hand. This trades photoreal
// detail for predictability and compile speed — Schneider's Nubis uses
// pre-baked 3D textures for the same effect; for our scale (sky-dome only,
// no fly-through), this is plenty.
const cloudHashNode = makeWgslFn(`
fn vc_hash(p: vec3<f32>) -> f32 {
  var q = fract(p * vec3<f32>(443.897, 441.423, 437.195));
  q = q + dot(q, q.yzx + vec3<f32>(19.19, 19.19, 19.19));
  return fract((q.x + q.y) * q.z);
}
`);

const cloudNoise3DNode = makeWgslFn(`
fn vc_noise3(p: vec3<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let f = f0 * f0 * (vec3<f32>(3.0, 3.0, 3.0) - 2.0 * f0);
  let n000 = vc_hash(i + vec3<f32>(0.0, 0.0, 0.0));
  let n100 = vc_hash(i + vec3<f32>(1.0, 0.0, 0.0));
  let n010 = vc_hash(i + vec3<f32>(0.0, 1.0, 0.0));
  let n110 = vc_hash(i + vec3<f32>(1.0, 1.0, 0.0));
  let n001 = vc_hash(i + vec3<f32>(0.0, 0.0, 1.0));
  let n101 = vc_hash(i + vec3<f32>(1.0, 0.0, 1.0));
  let n011 = vc_hash(i + vec3<f32>(0.0, 1.0, 1.0));
  let n111 = vc_hash(i + vec3<f32>(1.0, 1.0, 1.0));
  let nx00 = mix(n000, n100, f.x);
  let nx10 = mix(n010, n110, f.x);
  let nx01 = mix(n001, n101, f.x);
  let nx11 = mix(n011, n111, f.x);
  return mix(mix(nx00, nx10, f.y), mix(nx01, nx11, f.y), f.z);
}
`, [cloudHashNode]);

const cloudFbm3DNode = makeWgslFn(`
fn vc_fbm3(p: vec3<f32>) -> f32 {
  var q = p;
  var v = 0.0;
  var a = 0.5;
  v = v + vc_noise3(q) * a;
  q = q * 2.03;
  a = a * 0.5;
  v = v + vc_noise3(q) * a;
  q = q * 2.07;
  a = a * 0.5;
  v = v + vc_noise3(q) * a;
  q = q * 2.11;
  a = a * 0.5;
  v = v + vc_noise3(q) * a;
  return v / 0.9375;
}
`, [cloudNoise3DNode]);

// Worley-ish cellular noise — minimum distance to a hashed cell point gives
// the puffy cumulus shape. Inverted so dense cells are 1, voids are 0.
const cloudWorleyNode = makeWgslFn(`
fn vc_worley(p: vec3<f32>) -> f32 {
  let i = floor(p);
  let f = fract(p);
  var minDist = 1.0;
  for (var x: i32 = -1; x <= 1; x = x + 1) {
    for (var y: i32 = -1; y <= 1; y = y + 1) {
      for (var z: i32 = -1; z <= 1; z = z + 1) {
        let neighbor = vec3<f32>(f32(x), f32(y), f32(z));
        let cellPt = neighbor + vec3<f32>(
          vc_hash(i + neighbor),
          vc_hash(i + neighbor + vec3<f32>(13.7, 0.0, 0.0)),
          vc_hash(i + neighbor + vec3<f32>(0.0, 17.3, 0.0))
        );
        let d = length(f - cellPt);
        minDist = min(minDist, d);
      }
    }
  }
  return 1.0 - clamp(minDist, 0.0, 1.0);
}
`, [cloudHashNode]);

// Combined density: Worley base for the puffy cumulus shape, FBM for fine
// wisp detail, height profile to keep the slab finite, coverage threshold
// to control how cloudy the sky is.
const cloudDensityNode = makeWgslFn(`
fn vc_density(
  p: vec3<f32>,
  baseY: f32,
  topY: f32,
  coverage: f32,
  anvilBias: f32,
  windOffset: vec3<f32>
) -> f32 {
  let q = (p + windOffset) * 0.0010;
  let base = vc_worley(q * 0.6);
  let detail = vc_fbm3(q * 4.2);
  // Height profile — anvil shape (flat bottom, billowy mid, fading top).
  let heightT = clamp((p.y - baseY) / max(1.0, topY - baseY), 0.0, 1.0);
  let bottom = smoothstep(0.0, 0.18, heightT);
  let topFade = 1.0 - smoothstep(0.55, 1.0, heightT);
  let anvil = mix(1.0, smoothstep(0.0, 0.4, heightT), anvilBias);
  let heightProfile = bottom * topFade * anvil;
  // Coverage threshold: subtract coverage from the noise then remap, so
  // higher coverage values produce more dense pixels above zero.
  let shape = clamp(base * 0.7 + detail * 0.3, 0.0, 1.0);
  let cov = max(0.0, shape - (1.0 - coverage));
  return cov * heightProfile;
}
`, [cloudWorleyNode, cloudFbm3DNode]);

// Henyey-Greenstein phase function: forward-scattering bias when looking
// toward the sun (mu = dot(viewDir, sunDir) → 1) gives the bright halo
// around the sun through clouds; mu → -1 (looking away) is dim.
// g = 0.65 is "moderately forward-scattering," typical for water vapour.
const cloudPhaseNode = makeWgslFn(`
fn vc_phase(mu: f32, g: f32) -> f32 {
  let g2 = g * g;
  let denom = 1.0 + g2 - 2.0 * g * mu;
  return (1.0 - g2) / (4.0 * 3.14159 * pow(max(denom, 0.0001), 1.5));
}
`);

// The actual raymarcher. For each fragment, intersect the view ray with the
// slab top + bottom, march N steps with blue-noise dither, accumulate
// scattering + transmittance. Output is `(scatteredColor, alpha)`.
const cloudRaymarchNode = makeWgslFn(`
fn vc_raymarch(
  worldPos: vec3<f32>,
  cameraWorldPos: vec3<f32>,
  sunDir: vec3<f32>,
  sunColor: vec3<f32>,
  ambientColor: vec3<f32>,
  baseY: f32,
  topY: f32,
  coverage: f32,
  density: f32,
  anvilBias: f32,
  windOffset: vec3<f32>
) -> vec4<f32> {
  let rd = normalize(worldPos - cameraWorldPos);
  // Skip when looking down — we only render the slab above eye level. With
  // a back-side dome at radius 4200, the shader runs on the inside of the
  // sphere only when the camera is inside; this guard keeps the lower
  // hemisphere completely transparent.
  if (rd.y < 0.02) {
    return vec4<f32>(0.0, 0.0, 0.0, 0.0);
  }
  // Intersect ray with the top and bottom planes of the slab. Camera is
  // typically below baseY in the canyon; both intersections are positive.
  let tBottom = (baseY - cameraWorldPos.y) / max(rd.y, 0.0001);
  let tTop    = (topY  - cameraWorldPos.y) / max(rd.y, 0.0001);
  let tNear = max(0.0, min(tBottom, tTop));
  let tFar  = min(8000.0, max(tBottom, tTop));
  if (tFar - tNear < 1.0) {
    return vec4<f32>(0.0, 0.0, 0.0, 0.0);
  }
  let stepLen = (tFar - tNear) / 32.0;
  // Tiny dither offset along the ray — without it 32 steps shows banding,
  // with it the bands break into noise the eye doesn't lock onto.
  let dither = vc_hash(worldPos * 0.1);
  var t = tNear + stepLen * dither;
  var transmittance = 1.0;
  var scattered = vec3<f32>(0.0, 0.0, 0.0);
  let mu = dot(rd, sunDir);
  // Two-lobe phase mix: forward scattering for the sun halo + isotropic
  // for the ambient diffusion. Mostly forward.
  let phaseFwd = vc_phase(mu, 0.65);
  let phaseBack = vc_phase(mu, -0.15);
  let phase = mix(phaseFwd, phaseBack, 0.25) * 4.0;
  for (var i: i32 = 0; i < 32; i = i + 1) {
    let p = cameraWorldPos + rd * t;
    let d = vc_density(p, baseY, topY, coverage, anvilBias, windOffset);
    if (d > 0.001) {
      // Cheap shadow: 4 taps toward the sun, accumulating density.
      // Skip every other tap if perf becomes a concern.
      var lt = 0.0;
      for (var j: i32 = 1; j <= 4; j = j + 1) {
        let lp = p + sunDir * f32(j) * 70.0;
        lt = lt + vc_density(lp, baseY, topY, coverage, anvilBias, windOffset);
      }
      let lightT = exp(-lt * density * 0.55);
      let radiance = sunColor * lightT * phase + ambientColor * 0.45;
      let extinction = d * density;
      // Energy-conserving in-scattering. The mid-rule integrand approximation
      // gives noticeably crisper edges than a basic accumulation.
      let scatter = (radiance - radiance * exp(-extinction * stepLen)) / max(extinction, 0.0001);
      scattered = scattered + scatter * transmittance;
      transmittance = transmittance * exp(-extinction * stepLen);
      if (transmittance < 0.01) {
        break;
      }
    }
    t = t + stepLen;
  }
  return vec4<f32>(scattered, 1.0 - transmittance);
}
`, [cloudDensityNode, cloudPhaseNode, cloudHashNode]);

function colorToVec3(color: THREE.Color): THREE.Vector3 {
  return new THREE.Vector3(color.r, color.g, color.b);
}

function setVec3FromColor(target: THREE.Vector3, source: THREE.Color): void {
  target.set(source.r, source.g, source.b);
}

export function createVolumetricCloudDome(style: StyleConfig): VolumetricCloudDome {
  const sunDirInit = new THREE.Vector3(
    style.sky.sunDir.x,
    style.sky.sunDir.y,
    style.sky.sunDir.z,
  ).normalize();
  const sunColorInit = new THREE.Color(style.sky.horizonHex);
  const horizonInit = new THREE.Color(style.sky.horizonHex);
  const zenithInit = new THREE.Color(style.sky.zenithHex);

  const uniforms: VolumetricCloudUniforms = {
    uSunDir: makeNodeUniform(sunDirInit),
    uSunColor: makeNodeUniform(colorToVec3(sunColorInit)),
    uHorizonColor: makeNodeUniform(colorToVec3(horizonInit)),
    uZenithColor: makeNodeUniform(colorToVec3(zenithInit)),
    // 0.62 reads as scattered cumulus — broken cloud cover, lots of sky
    // visible between puffs. 0.45 is wispy; 0.85+ approaches overcast.
    uCoverage: makeNodeUniform(0.62),
    // 0.30 makes the puffs read as solid cumulus with crisp under-shadows;
    // lower (0.10) gives wispy translucent clouds.
    uDensity: makeNodeUniform(0.30),
    // Wind speed in metres per second along +X. Drives the time-based
    // windOffset that translates the noise field through the slab.
    uWindSpeed: makeNodeUniform(8.0),
    // Anvil bias — 0 = soft cumulus, 1 = flat-bottomed thunderhead. We
    // want soft cumulus most of the time.
    uAnvilBias: makeNodeUniform(0.25),
  };

  // Render with alpha-to-coverage so the cloud paints over the sky in
  // dense pixels and discards where transmittance is high. Opaque batch
  // (transparent: false) so renderOrder works against the opaque sky and
  // the depth test stays well-defined. `alphaTest` cuts out the ray-misses
  // and clear-sky gaps; without it the dome would paint a solid sphere of
  // ambient cloud over the entire sky.
  const material = new MeshBasicNodeMaterial({
    side: THREE.BackSide,
    transparent: false,
    depthWrite: false,
    depthTest: true,
    toneMapped: true,
    fog: false,
    alphaTest: 0.01,
  });

  // Build the wind offset as a TSL vec3 expression so the noise field
  // translates through the slab over time. The WGSL function expects
  // `windOffset: vec3<f32>` — passing a scalar silently corrupts the cell
  // coords and the density field reads as constant near-zero (no clouds).
  // Wind drifts mostly along +X with a small +Z component so the cloud bank
  // doesn't read as a perfectly axis-aligned conveyor belt.
  const tNode = (time as any).mul(uniforms.uWindSpeed);
  const windOffset = (vec3 as any)(tNode, 0, tNode.mul(0.32));

  const result = cloudRaymarchNode({
    worldPos: positionWorld,
    cameraWorldPos: cameraPosition,
    sunDir: uniforms.uSunDir,
    sunColor: uniforms.uSunColor,
    ambientColor: uniforms.uHorizonColor,
    baseY: makeNodeUniform(BASE_Y) as any,
    topY: makeNodeUniform(TOP_Y) as any,
    coverage: uniforms.uCoverage,
    density: uniforms.uDensity,
    anvilBias: uniforms.uAnvilBias,
    windOffset,
  });

  material.colorNode = result.rgb;
  material.opacityNode = result.a;
  material.userData.volumetricCloudUniforms = uniforms;
  // Mark as WebGPU-safe so the compatibility report doesn't flag it as a
  // custom shader material that still needs porting.
  material.userData.aerialPerspectiveWebGPUFallback = true;
  material.userData.valueCompositionWebGPUFallback = true;

  // Reuse a single sphere geometry for the dome. Low subdivision is fine
  // since the shader does the work — the geometry is just a UV-sphere shell
  // for the rasteriser to fill with the raymarch.
  const geometry = new THREE.SphereGeometry(DOME_RADIUS, 24, 16);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'volumetric-cloud-dome';
  mesh.frustumCulled = false;
  // Render before the cloud cards (-450) but after the gradient sky (-1000),
  // so the cards still composite on top for crisp parallax silhouettes near
  // the player while the dome provides the deep volumetric backdrop.
  mesh.renderOrder = -900;
  tagValueBucket(mesh, 'bg');

  return {
    mesh,
    applyStyle(nextStyle) {
      // Pull live sun direction from the shared aerial uniforms — that way
      // we follow whatever installAerialPerspectiveStyle just normalised
      // rather than re-normalising here.
      (uniforms.uSunDir.value as THREE.Vector3).copy(SHARED_AERIAL_UNIFORMS.uSunDirWorld.value);
      // Sun colour comes from the warm horizon hex when followSky is on,
      // otherwise the explicit sun colour. The cloud underside picks this
      // up via the forward-scattering lobe.
      const sunCol = new THREE.Color(nextStyle.sky.horizonHex);
      setVec3FromColor(uniforms.uSunColor.value as THREE.Vector3, sunCol);
      setVec3FromColor(uniforms.uHorizonColor.value as THREE.Vector3, new THREE.Color(nextStyle.sky.horizonHex));
      setVec3FromColor(uniforms.uZenithColor.value as THREE.Vector3, new THREE.Color(nextStyle.sky.zenithHex));
    },
    update(_dt) {
      // Wind animates via the TSL `time` node automatically; nothing to do.
      // dt is here so callers can share the same per-frame contract as
      // CloudField/SfxRuntime.
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
